import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from '../../lib/cn'
import { LoadingSpinner } from './LoadingSpinner'

type Variant = 'black-pill' | 'white-pill' | 'glass-dark' | 'glass-light'
type Size = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
}

const base =
  'inline-flex items-center justify-center gap-2 rounded-pill font-sans font-450 transition-colors disabled:cursor-not-allowed focus:outline-none'

const variants: Record<Variant, string> = {
  // Primary CTA on light surfaces — brand blue pill
  'black-pill':
    'bg-[#2183d8] text-white hover:bg-[#1c72bd] active:bg-[#185f9e] focus-visible:outline-[#2183d8] disabled:bg-black/[0.08] disabled:text-black/30 disabled:hover:bg-black/[0.08]',
  // Primary CTA on dark / colored surfaces — solid white pill
  'white-pill':
    'bg-white text-black border border-black/[0.06] hover:bg-black/[0.03] active:bg-black/[0.05] disabled:bg-white/60 disabled:text-black/30',
  // Secondary on light surfaces — glass dark
  'glass-dark':
    'bg-black/[0.04] text-black hover:bg-black/[0.08] active:bg-black/[0.12] disabled:bg-black/[0.025] disabled:text-black/25 disabled:hover:bg-black/[0.025]',
  // Secondary on dark / colored surfaces — glass light
  'glass-light':
    'bg-white/[0.16] text-white hover:bg-white/[0.24] active:bg-white/[0.32] disabled:bg-white/[0.08] disabled:text-white/35',
}

const sizes: Record<Size, string> = {
  sm: 'px-4 py-1.5 text-body-sm',
  md: 'px-5 py-2 text-body',
  lg: 'px-7 py-3 text-body-lg',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant = 'black-pill',
    size = 'md',
    type = 'button',
    loading,
    leadingIcon,
    trailingIcon,
    disabled,
    children,
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(base, variants[variant], sizes[size], className)}
      disabled={disabled || loading}
      type={type}
      {...props}
    >
      {loading ? (
        <LoadingSpinner size="sm" className="border-current/20 border-t-current" />
      ) : (
        leadingIcon
      )}
      <span className="inline-flex items-center justify-center">{children}</span>
      {!loading && trailingIcon}
    </button>
  )
})
